/**
 * Supabase Teams Service
 * Handles all team-related operations with Supabase
 */

import { supabase } from './client';

export interface SupabaseTeam {
  id: string;
  legacy_id: string;
  team_name: string | null;
  description: string | null;
  report_name_prefix: string | null;
  manager_users: string | null;
  member_users: string | null;
  unit_id: string | null;
  comp_id: string | null;
  sync_source: string;
  synced_at: string;
  created_at: string;
  updated_at: string;
}

export interface TeamFilters {
  team_name?: string;
  unit_id?: string;
  comp_id?: string;
  manager_users?: string;
  search?: string;
}

export interface PaginationOptions {
  page?: number;
  limit?: number;
}

export interface TeamResponse {
  data: SupabaseTeam[];
  count: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

class TeamService {
  /**
   * Get teams with filtering and pagination
   */
  async getTeams(
    filters: TeamFilters = {},
    pagination: PaginationOptions = {}
  ): Promise<TeamResponse> {
    const { page = 1, limit = 50 } = pagination;
    const offset = (page - 1) * limit;

    let query = supabase
      .from('teams')
      .select('*', { count: 'exact' })
      .order('team_name', { ascending: true });

    // Apply filters
    if (filters.team_name) {
      query = query.ilike('team_name', `%${filters.team_name}%`);
    }

    if (filters.unit_id) {
      query = query.eq('unit_id', filters.unit_id);
    }

    if (filters.comp_id) {
      query = query.eq('comp_id', filters.comp_id);
    }

    if (filters.manager_users) {
      query = query.ilike('manager_users', `%${filters.manager_users}%`);
    }

    if (filters.search) {
      query = query.or(
        `team_name.ilike.%${filters.search}%,description.ilike.%${filters.search}%,manager_users.ilike.%${filters.search}%`
      );
    }

    // Apply pagination
    query = query.range(offset, offset + limit - 1);

    const { data, error, count } = await query;

    if (error) {
      throw error;
    }

    const totalPages = Math.ceil((count || 0) / limit);

    return {
      data: data || [],
      count: count || 0,
      page,
      limit,
      totalPages,
      hasNextPage: page < totalPages,
      hasPrevPage: page > 1,
    };
  }

  /**
   * Get a single team by ID (Supabase UUID)
   */
  async getTeamById(id: string): Promise<SupabaseTeam | null> {
    const { data, error } = await supabase
      .from('teams')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // Team not found
      }
      throw error;
    }

    return data;
  }

  /**
   * Get a single team by legacy ID (SQL Server ID)
   */
  async getTeamByLegacyId(legacyId: string): Promise<SupabaseTeam | null> {
    const { data, error } = await supabase
      .from('teams')
      .select('*')
      .eq('legacy_id', legacyId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // Team not found
      }
      throw error;
    }

    return data;
  }

  /**
   * Get distinct unit IDs
   */
  async getUnits(): Promise<string[]> {
    const { data, error } = await supabase
      .from('teams')
      .select('unit_id')
      .not('unit_id', 'is', null)
      .order('unit_id');

    if (error) {
      throw error;
    }

    // Get unique unit IDs
    const uniqueUnits = [...new Set(data.map(item => item.unit_id))].filter(Boolean);
    return uniqueUnits as string[];
  }

  /**
   * Get distinct company IDs
   */
  async getCompanies(): Promise<string[]> {
    const { data, error } = await supabase
      .from('teams')
      .select('comp_id')
      .not('comp_id', 'is', null)
      .order('comp_id');

    if (error) {
      throw error;
    }

    // Get unique company IDs
    const uniqueCompanies = [...new Set(data.map(item => item.comp_id))].filter(Boolean);
    return uniqueCompanies as string[];
  }

  /**
   * Get teams statistics
   */
  async getTeamsStats(): Promise<{
    totalTeams: number;
    uniqueUnits: number;
    uniqueCompanies: number;
    teamsWithManagers: number;
    latestSyncDate: string | null;
  }> {
    // Get basic stats
    const { count: totalTeams } = await supabase
      .from('teams')
      .select('*', { count: 'exact', head: true });

    const units = await this.getUnits();
    const companies = await this.getCompanies();

    const { count: teamsWithManagers } = await supabase
      .from('teams')
      .select('*', { count: 'exact', head: true })
      .not('manager_users', 'is', null)
      .neq('manager_users', '');

    const { data: latestSync } = await supabase
      .from('teams')
      .select('synced_at')
      .order('synced_at', { ascending: false })
      .limit(1)
      .single();

    return {
      totalTeams: totalTeams || 0,
      uniqueUnits: units.length,
      uniqueCompanies: companies.length,
      teamsWithManagers: teamsWithManagers || 0,
      latestSyncDate: latestSync?.synced_at || null,
    };
  }

  /**
   * Search teams by text
   */
  async searchTeams(
    searchTerm: string,
    pagination: PaginationOptions = {}
  ): Promise<TeamResponse> {
    return this.getTeams(
      { search: searchTerm },
      pagination
    );
  }

  /**
   * Sync teams từ SQL Server
   */
  static async syncTeamsFromSqlServer(): Promise<{
    success: boolean;
    processed: number;
    errors: number;
    error_details?: string[];
  }> {
    // This will be called from the sync script
    // For now, return a placeholder response
    throw new Error('Use sync-teams script to sync data');
  }
}

export const teamService = new TeamService();
export default teamService;